import React, { createContext, useContext, useState, useEffect } from 'react';

const PHOTO_STORAGE_KEY = 'securecoop_profile_photo';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      setLoading(false);
      return;
    }
    const validateAndRestore = async () => {
      try {
        const { getCurrentUser } = await import('../api/api');
        const current = await getCurrentUser();
        if (current) {
          const parsed = JSON.parse(userData);
          const restored = {
            email: current.email,
            name: current.name,
            role: current.role,
            organizationId: current.organizationId,
            canBypassPhishingVerification: current.canBypassPhishingVerification === true || current.role === 'ADMIN',
          };
          const photo = localStorage.getItem(PHOTO_STORAGE_KEY);
          if (photo) restored.profilePhoto = photo;
          setUser(restored);
          localStorage.setItem('user', JSON.stringify({ ...restored, profilePhoto: undefined }));
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };
    validateAndRestore();
  }, []);

  const login = (data) => {
    const userData = {
      email: data.email,
      name: data.name,
      role: data.role,
      organizationId: data.organizationId,
      canBypassPhishingVerification: data.canBypassPhishingVerification === true,
    };
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateOrganization = (organizationId) => {
    if (user) {
      const updated = { ...user, organizationId };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    }
  };

  const updateUser = (updates) => {
    if (user) {
      const updated = { ...user, ...updates };
      if (updates.profilePhoto != null) {
        localStorage.setItem(PHOTO_STORAGE_KEY, updates.profilePhoto || '');
      }
      setUser(updated);
      const toPersist = { ...updated };
      delete toPersist.profilePhoto;
      localStorage.setItem('user', JSON.stringify(toPersist));
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateOrganization, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
